import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionItemBodyWithAnimation,
  AccordionItemHeading,
  Box,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import slugify from 'shared/utils/slugify';

const Title = styled(Text)`
  ${({ theme: { media, spacing } }) => css`
    margin: 0 auto ${spacing.s4};
    width: 100%;
    text-align: center;

    ${media.up('big')} {
      margin-bottom: ${spacing.s7};
    }
  `}
`;

const TechSpecItem = styled(Box)`
  ${({ theme: { media } }) => css`
    flex-direction: column;
    ${media.up('slim')} {
      flex-direction: row;
    }
  `}
`;

const TechSpecName = styled(Text)`
  ${({ theme: { media, spacing } }) => css`
    margin-bottom: ${spacing.s1};
    ${media.up('slim')} {
      margin-bottom: 0;
    }
  `}
`;

const TechSpecDescription = styled(Text)`
  ${({ theme: { palette } }) => css`
    color: ${palette.baseColors.mawheranui};
  `}
`;

export default function TechSpecsAccordion() {
  const { selectedDevice } = useProductDetails();
  const specificationGroups = selectedDevice.productSpecificationGroups;
  const [currentExpandedItems, setCurrentExpandedItems] = useState<string[]>(
    [],
  );

  if (!specificationGroups) {
    return null;
  }

  return (
    <LinkRegion regionId="specifications">
      <Title variant="headingS">Specifications</Title>
      <Accordion
        allowMultipleExpanded
        onChange={(expandedItems: string[]) => {
          setCurrentExpandedItems(expandedItems);
          trackCustomLink(expandedItems?.[0]);
        }}
      >
        {specificationGroups.map((specificationGroup) => {
          const { group, specifications } = specificationGroup;
          const id = slugify(group);

          return (
            <AccordionItem
              key={id}
              uuid={id}
              isOpen={currentExpandedItems.includes(id)}
            >
              <AccordionItemHeading>
                <Text variant="subheadingM">{group}</Text>
                <AccordionButton />
              </AccordionItemHeading>
              <AccordionItemBodyWithAnimation
                isOpen={currentExpandedItems.includes(id)}
              >
                {specifications.map((spec) => (
                  <TechSpecItem
                    key={spec.name}
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    mb="s4"
                  >
                    <TechSpecName variant="bodySDemi">{spec.name}</TechSpecName>
                    <TechSpecDescription variant="bodySMedium">
                      {spec.value}
                    </TechSpecDescription>
                  </TechSpecItem>
                ))}
              </AccordionItemBodyWithAnimation>
            </AccordionItem>
          );
        })}
      </Accordion>
    </LinkRegion>
  );
}
